<template>
    <v-container>
        <v-row>
            <v-col sm="4" md="4" lg="4">
                <v-select
                    v-model="clubId"
                    :items="clubs"
                    item-text="name"
                    item-value="id"
                    :loading="loadingClubs"
                    label="Select a Club"
                />
            </v-col>

            <v-col sm="3" md="3" lg="3">
                <v-checkbox v-model="state.showQualified" label="Show Qualified" />
            </v-col>

            <v-col
                class="text-right mr-4"
                :class="{
                    'text-h6': $vuetify.breakpoint.mdAndUp,
                    'font-weight-bold': $vuetify.breakpoint.smAndDown,
                    'mt-5': $vuetify.breakpoint.smAndDown,
                    'mt-4': $vuetify.breakpoint.mdAndUp,
                }"
            >
                <v-icon class="mt-n1">mdi-clock-time-four-outline</v-icon>
                {{ currentTime.format('ddd, M/D h:mm a') }}
            </v-col>
        </v-row>

        <v-row>
            <v-col>
                <!-- Show skeleton loader when schedule is loading -->
                <div v-if="loadingSchedule">
                    <v-skeleton-loader type="table" />
                </div>

                <!-- Otherwise display the schedule -->
                <div v-else>
                    <div v-for="(groupedSession, groupedSessionIndex) in groupedSessions" :key="groupedSession[0].id">
                        <h6 class="text-h6 my-2">
                            {{ groupedSession[0].day(meet) }} Session {{ groupedSession[0].ordinalWithTime(meet) }}
                        </h6>

                        <v-simple-table class="collapsible-table">
                            <template #default>
                                <thead>
                                    <tr>
                                        <th>Flight</th>
                                        <th>TRA</th>
                                        <th>DMT</th>
                                        <th>TUM</th>
                                        <th v-if="showSynColumn(groupedSession)">SYN</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <club-schedule-flights
                                        v-for="(flight, index) in flightShells(groupedSession)"
                                        :id="`flight-${groupedSessionIndex}-${index}`"
                                        :key="index"
                                        :flight-title="flight.title"
                                        :flight-time="flight.time"
                                        :sessions="groupedSession"
                                        :active="flightIsActive(flight)"
                                        :showing-syn-column="showSynColumn(groupedSession)"
                                    />
                                </tbody>
                            </template>
                        </v-simple-table>
                    </div>
                </div>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import groupBy from 'lodash/groupBy.js';
import values from 'lodash/values.js';
import uniqBy from 'lodash/uniqBy.js';
import Vue from 'vue';
import Club from '@/Models/Club.js';
import Session from '@/Models/Session.js';
import ClubScheduleFlights from '@/Components/Club/Schedule/ClubScheduleFlights.vue';
import Meet from '@/Models/Meet.js';
import User from '@/Models/User.js';
import Flight from '@/Models/Flight.js';
import StartList from '@/Models/StartList.js';

const sharedState = Vue.observable({
    showQualified: true,
    qualifications: {},
});

export default {
    name: 'ClubSchedule',

    components: { ClubScheduleFlights },

    props: {
        meet: {
            type: Meet,
            required: true,
        },
    },

    data: () => ({
        clubId: null,
        loadingClubs: false,
        loadingSchedule: false,
        openPanels: [],
        currentTime: null,
        timer: null,
        state: sharedState,
    }),

    provide() {
        return {
            state: this.state,
        };
    },

    computed: {
        user() {
            return User.query().with('affiliations.affiliate').find(this.$page.props.auth.user?.id);
        },

        clubs() {
            return Club.query().orderBy('name').get();
        },

        sessions() {
            return Session.query()
                .with(['flights.start_lists.athlete', 'flights.start_lists.athlete2'])
                .orderBy('order')
                .get();
        },

        /**
         * Combine sessions (different equipment under the same session) into a single session.
         * Session 1A & 1B => Session 1
         */
        groupedSessions() {
            return values(groupBy(this.sessions, 'ordinal'));
        },

        activeFlight() {
            if (!this.currentTime) {
                return null;
            }

            let activeFlight = null;

            this.groupedSessions.forEach((groupedSession, sessionIndex) => {
                this.flightShells(groupedSession).forEach((flight, flightIndex) => {
                    if (this.currentTime.isBetween(flight.startsAt, flight.endsAt)) {
                        activeFlight = { flight, sessionIndex, flightIndex };
                    }
                });
            });

            return activeFlight;
        },
    },

    watch: {
        clubId(clubId) {
            if (clubId) {
                Session.deleteAll();
                Flight.deleteAll();
                StartList.deleteAll();

                this.fetchSchedule(clubId);
            }
        },

        clubs(clubs) {
            if (this.user?.affiliations?.length > 0 && !this.clubId && clubs.length > 0) {
                this.user.affiliations
                    .filter((affiliation) => affiliation.affiliate instanceof Club)
                    .forEach((affiliation) => {
                        if (clubs.some((club) => club.id === affiliation.affiliate.id)) {
                            this.clubId = affiliation.affiliate.id;
                        }
                    });
            }
        },

        groupedSessions: {
            handler(newSessions, oldSessions) {
                if (newSessions.length > 0 && newSessions.length !== oldSessions.length) {
                    this.scrollToActiveFlight();
                }
            },
            immediate: false, // Run at creation if needed, but typically handled in mounted or created hooks
        },
    },

    async created() {
        this.currentTime = this.$moment.utc().tz(this.meet.timezone);

        this.timer = setInterval(() => {
            this.currentTime = this.$moment.utc().tz(this.meet.timezone);
        }, 30 * 1000);

        await this.fetchClubs();

        if (this.clubId && (this.meet.jo_qualification_meet || this.meet.elite_qualification_meet)) {
            await this.loadQualifiers();
            this.showQualified = true;
        }
    },

    beforeDestroy() {
        clearInterval(this.timer);
    },

    methods: {
        async fetchClubs() {
            this.loadingClubs = true;

            await Club.api().get(`/api/meets/${this.meet.id}/competing-clubs`);

            this.loadingClubs = false;
        },

        async fetchSchedule(clubId) {
            this.loadingSchedule = true;

            await Session.api().get(`/api/meets/${this.meet.id}/club-schedule?club_id=${clubId}`);

            this.openPanels = this.sessions.map((session, index) => index);

            this.loadingSchedule = false;
        },

        flightShells(groupedSessions) {
            const flights = groupedSessions.flatMap((groupedSession, sessionIndex) =>
                groupedSession.flights
                    .map((flight, flightIndex) => {
                        const startsAt = flight.timestamp('starts_at').tz(this.meet.timezone);

                        if (!flight.original_title && !flight.starts_at && flight.start_lists?.length === 0) {
                            return null;
                        }

                        return {
                            title: flight.original_title ? flight.title : '',
                            time: flight.starts_at ? startsAt.format('h:mm a') : '',
                            startsAt,
                            endsAt: flight.timestamp('ends_at').tz(this.meet.timezone),
                            // New key: integer timestamp from moment object
                            timestamp: flight.starts_at ? startsAt.valueOf() : 0,
                            id: `flight-${sessionIndex}-${flightIndex}`,
                        };
                    })
                    .filter((flight) => flight),
            );

            // Sort flights by the timestamp key
            flights.sort((a, b) => a.timestamp - b.timestamp);

            return uniqBy(flights, 'title');
        },

        flightIsActive(flightShell) {
            return this.activeFlight?.flight?.startsAt.isSame(flightShell.startsAt);
        },

        async scrollToActiveFlight() {
            this.groupedSessions.forEach((groupedSession, sessionIndex) => {
                this.flightShells(groupedSession).forEach((flight, flightIndex) => {
                    if (this.flightIsActive(flight)) {
                        setTimeout(() => {
                            this.$scrollTo(`#flight-${sessionIndex}-${flightIndex}`, 500, {
                                offset: -window.innerHeight / 2,
                            });
                        }, 1000);
                    }
                });
            });
        },

        async loadQualifiers() {
            const { year } = this.meet;
            const { data } = await axios.get(`/api/clubs/${this.clubId}/qualifiers?year=${year}`);

            this.state.qualifications = data.data[year];
        },

        showSynColumn(groupedSession) {
            return groupedSession.some((session) => session.flights.some((flight) => flight.event === 'TS'));
        },
    },
};
</script>

<style lang="scss">
/** Apply border-collapse: collapse to the vuetify table */
.collapsible-table table {
    border-collapse: collapse !important;
}

.collapsible-table th,
.collapsible-table td {
    border: 1px solid #404040;
}
</style>
